/*
 * The signed-in layout: sidebar (lg+), top bar, banner slot and the page container around <Outlet />.
 * Pages render a <PageHeader> and panels; the container gives max width and responsive gutters.
 * Print: the shell's chrome (sidebar, top bar, banners, toasts) is `print:hidden` and the page prints on
 * white without gutters; a page hides its own extras with `print:hidden` (see the invoice page).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'
import { storage } from '@/lib/storage'
import { Banners } from './banners/Banners'
import { CommandPalette, useCommandPalette } from './CommandPalette'
import { MobileNav } from './MobileNav'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'

const COLLAPSE_KEY = 'bpanel.sidebar.collapsed'

export function AppShell() {
  const { t } = useTranslation('shell')
  const [collapsed, setCollapsed] = useState(() => storage.get(COLLAPSE_KEY) === '1')
  const [menu, setMenu] = useState(false)
  const palette = useCommandPalette()
  const toggle = () => {
    setCollapsed((c) => {
      storage.set(COLLAPSE_KEY, c ? '0' : '1')
      return !c
    })
  }
  return (
    <div className="flex min-h-dvh bg-app-canvas print:block print:min-h-0 print:bg-white">
      <a
        href="#main"
        className="sr-only z-[80] rounded-xl bg-surface px-4 py-3 text-14 font-semibold text-brand-700 shadow-lg focus:not-sr-only focus:fixed focus:start-4 focus:top-3 print:hidden"
      >
        {t('a11y.skip')}
      </a>
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <div className="flex min-w-0 flex-1 flex-col lg:border-s lg:border-hairline/70 print:block print:border-0">
        <TopBar onMenu={() => setMenu(true)} onSearch={() => palette.setOpen(true)} />
        <main id="main" tabIndex={-1} className="flex-1 outline-none">
          <div className="page-container flex flex-col gap-6 pt-4 pb-16 sm:pt-6 print:max-w-none print:p-0">
            <div className="contents print:hidden">
              <Banners />
            </div>
            <div>
              <Outlet />
            </div>
          </div>
        </main>
      </div>
      <MobileNav open={menu} onOpenChange={setMenu} />
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
    </div>
  )
}
